import { convertUnitsFinal } from "../../../../utils/dataAndFormulas/effluentForms";

// Lógica de armado de datos para "Evolución industrial", "Intensidad
// industrial", "Evolución doméstica" e "Intensidad doméstica" de
// EffluentKPIsDashBoard.js, extraída a un módulo aparte (sin JSX/hooks)
// para poder reusarla también desde el export a PDF (pdfEffluentCharts.js)
// sin duplicar la lógica ni crear un import circular con
// EffluentKPIsDashBoard.js. Mismo patrón que WaterKPIsDashboard/waterChartData.js.
//
// Nota: el código original ya tiene una inconsistencia propia (no
// introducida acá) en qué años usa cada gráfico — Comparación
// Industrial/Doméstica e Intensidad Industrial usan TODOS los años de
// resultsByYear sin recortar, mientras que Intensidad Doméstica sí recorta
// a los últimos 3. Se replica tal cual, sin "corregir".

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildYearColorMap(years) {
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
  };
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1",
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}

export function buildSharedYScale(...dataArrays) {
  const combinedMax = Math.max(
    0,
    ...dataArrays.flatMap((arr) => arr.map((d) => d.value || 0)),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  return { domainMin: 0, domainMax: yDomainMax, nice: true };
}

// "Evolución industrial"/"Evolución doméstica" — mismo cálculo para los
// dos, solo cambia el campo (industrial_effluents / domestic_effluents).
function buildComparisonData(resultsByYear, t, field) {
  const years = Object.keys(resultsByYear || {}).sort((a, b) => b - a);
  const bar = [];
  const line = [];

  years.forEach((year) => {
    const values = months.map((month) => resultsByYear?.[year]?.[month]?.[field]?.total || 0);
    const validValues = values.filter((v) => v > 0);
    const avg = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : 0;

    months.forEach((month, i) => {
      // Sin dato ese mes → no se empuja la barra (mismo criterio que
      // buildWaterComparisonData en waterChartData.js: values[i]===0 no
      // entra al dataset, así la barra no se dibuja ni aparece en el
      // tooltip agrupado, en vez de mostrarse en 0). Si el año entero no
      // tiene ningún mes cargado, tampoco se traza la línea de Promedio.
      if (values[i] > 0) {
        bar.push({ year: String(year), month: t(month), value: values[i], type: `Consumo ${year}` });
      }
      if (validValues.length > 0) {
        line.push({ year: String(year), month: t(month), value: avg, type: `--Promedio ${year}` });
      }
    });
  });

  return { years, barData: bar, lineData: line, yearColorMap: buildYearColorMap(years) };
}

export function buildEffluentComparisonIndustrialData(resultsByYear, t) {
  return buildComparisonData(resultsByYear, t, "industrial_effluents");
}

export function buildEffluentComparisonDomesticData(resultsByYear, t) {
  return buildComparisonData(resultsByYear, t, "domestic_effluents");
}

// "Intensidad industrial"/"Intensidad doméstica" — mismo cálculo para los
// dos (efluente / divisor, divisor = producción o trabajadores según cuál).
function buildMonthlyData(resultsByYear, t, field, divisorSource, years) {
  const cData = [];
  const aData = [];

  years.forEach((year) => {
    const totals = months.map((m) => {
      const entry = resultsByYear?.[year]?.[m]?.[field];
      const effluent = convertUnitsFinal(entry?.total || 0, entry?.unit, "L", field);
      const divisor = divisorSource?.[year]?.[m] || 0;
      // `null` (no 0) cuando falta el divisor — mismo criterio que
      // buildWaterProductionIntensityData en waterChartData.js: un 0
      // dibuja el punto pegado al eje (parece que el mes tuvo intensidad
      // cero), mientras que `null` hace que el gráfico deje el hueco sin
      // marcar nada, que es lo correcto cuando no hay dato.
      return divisor > 0 && effluent > 0 ? effluent / divisor : null;
    });
    const validValues = totals.filter((v) => v !== null && !isNaN(v) && v > 0);
    const avg = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : 0;

    months.forEach((month, i) => {
      cData.push({ year: String(year), month: t(month), value: totals[i], tipo: "Consumo", type: `Consumo ${year}` });
      aData.push({ year: String(year), month: t(month), value: validValues.length > 0 ? avg : null, tipo: "Promedio", type: `--Promedio ${year}` });
    });
  });

  return { years, consumptionData: cData, averageData: aData, yearColorMap: buildYearColorMap(years) };
}

export function buildEffluentIntensityIndustrialData(resultsByYear, resultsProductions, t) {
  const years = Object.keys(resultsByYear || {}).sort((a, b) => b - a); // TODOS los años
  return buildMonthlyData(resultsByYear, t, "industrial_effluents", resultsProductions, years);
}

export function buildEffluentIntensityDomesticData(resultsByYear, resultsWorkers, t) {
  const allYears = Object.keys(resultsByYear || {}).sort((a, b) => b - a);
  const years = allYears.slice(0, 3); // recorta a los últimos 3
  return buildMonthlyData(resultsByYear, t, "domestic_effluents", resultsWorkers, years);
}
